import { IFontStyle, IFontFamilyStyle } from '@core/domain';
import { replaceOrigin, getUrlParameters } from '../core/ad/utils/url';

export function isElementDescendantOfElement(parentElement: HTMLElement | Element | EventTarget | null, childElement: any): boolean {

    if (!parentElement) {
        return false;
    }

    if (parentElement === childElement) {
        return true;
    }

    let node = childElement.parentNode;
    while (node) {
        if (node === parentElement) {
            return true;
        }
        node = node.parentNode;
    }

    return false;
}

interface IUrlOption {
    scheme?: 'http:' | 'https:';
    host?: string;
    port?: number;
    pathPrefix?: string;
    path?: string;
}

export function formatUrl(option: IUrlOption): string {
    const scheme = option.scheme || 'http:';
    const host = option.host || 'localhost';
    const port = option.port;
    const pathPrefix = option.pathPrefix || '';
    const path = option.path || '';

    return scheme + '//' + host + (port ? ':' + port : '') + pathPrefix + path;
}

export function isElementDescendantOfElementWithClass(element: any, className: string): boolean {
    if (element.classList && element.classList.contains(className)) {
        return true;
    }
    let node = element.parentNode;
    while (node) {
        if (node.classList && (node as HTMLElement).classList.contains(className)) {
            return true;
        }
        node = node.parentNode;
    }
    return false;
}

export function isChildOfSelector(element: HTMLElement, parentSelector: string): boolean {
    let node = element.parentNode as HTMLElement;
    while (node) {
        if (typeof node.matches !== 'undefined' && node.matches(parentSelector)) {
            return true;
        }
        node = node.parentNode as HTMLElement;
    }
    return false;
}

export function isFocused(el: HTMLElement): boolean {
    return el === document.activeElement;
}

const fontStyleMap = new Map<string, FontFace>();
export async function injectFontFace(fontStyle: IFontStyle | IFontFamilyStyle, fontServiceOrigin?: string): Promise<FontFace> {
    const fontURL = getFontURL(fontStyle, fontServiceOrigin);

    let fontFace = fontStyleMap.get(fontStyle.id);
    if (fontFace) {
        return await fontFace.load();
    }

    if (fontURL) {
        fontFace = new FontFace('f-' + fontStyle.id, `url(${fontURL})`);
    } else {
        throw new Error('Unknown font style.');
    }
    fontStyleMap.set(fontStyle.id, fontFace);
    return await fontFace.load();
}

export function injectFontStyle(document: Document, fontStyle: IFontStyle | IFontFamilyStyle , fontServiceOrigin?: string): void {
    const fontURL = getFontURL(fontStyle, fontServiceOrigin);
    if (!fontURL) {
        throw new Error('Failed to get URL of font style');
    }

    const fontFamily = 'f-' + fontStyle.id;
    const fontType = getFontExtension(fontURL);
    const fontStyleSheet = document.createElement('style');
    const fontPreloadLink = document.createElement('link');
    fontPreloadLink.rel = 'preload';
    fontPreloadLink.href = fontURL;
    fontPreloadLink.as = 'font';
    fontPreloadLink.type = 'font/' + fontType;
    fontPreloadLink.crossOrigin = '';

    fontStyleSheet.textContent = `
                        @font-face {
                          font-family: '${fontFamily}';
                          src: url(${fontURL}) format('${fontType}');
                        }
                      `;
    document.head.appendChild(fontPreloadLink);
    document.head.appendChild(fontStyleSheet);
}

export function getFontExtension(url: string): string | undefined {
    return getUrlParameters(url).u?.split('.').pop();
}

export function getFontURL(fontStyle: IFontStyle | IFontFamilyStyle, fontServiceOrigin?: string): string {
    let url = (fontStyle as IFontStyle).src || (fontStyle as IFontFamilyStyle).fontUrl;

    if ((typeof process === 'undefined' || (!process.env.IN_TEST && process.env.STUDIO_JS)) && window.bfstudio) {
        url = replaceOrigin(url, process.env.FONTS_AZURE_STORAGE_CDN_ORIGIN!);
    }
    if ((typeof process === 'undefined' || !process.env.IN_TEST) && fontServiceOrigin) {
        // notice, url can be base64 and still work
        url = replaceOrigin(url, fontServiceOrigin);
    }
    return url;
}

/**
 * Zero width joints are used for joining spans that are separated. In Arabic, Hindi and Emoji text, glyphs that are in separate
 * spans are rendered as separate instead of continous joined text. The glyphs changes a bit when they are separated vs joined.
 * In order to support Character Styles, we have to include zero-width joint character u+200D and zero-width-no-joint u+200C
 * character, to signal that a text is joined.
 */
export function getStringByExcludingZeroWidthJoints(node: Node): string {
    return node.textContent!.replace(/\u200C/g, '').replace(/\u200D/g, '');
}
